import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useNavigate } from 'react-router-dom';
import {
  format, addDays, addWeeks, subWeeks, addMonths, subMonths,
  startOfWeek, endOfWeek, startOfMonth, endOfMonth,
} from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Calendar, Clock, MapPin, ChevronLeft, ChevronRight, List, LayoutGrid, CalendarDays, CalendarRange } from 'lucide-react';
import { toast } from 'sonner';
import { StaffRosterCalendar } from '@/components/portal/StaffRosterCalendar';

const VIEW_OPTS = [
  { id: 'week',  label: 'Week',  Icon: LayoutGrid },
  { id: 'day',   label: 'Day',   Icon: CalendarDays },
  { id: 'month', label: 'Month', Icon: CalendarRange },
  { id: 'list',  label: 'List',  Icon: List },
];

export default function PortalRosterPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [myShifts, setMyShifts] = useState([]);
  const [openShifts, setOpenShifts] = useState([]);
  const [calendarEnabled, setCalendarEnabled] = useState(true);
  const [view, setView] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        const [myRes, openRes] = await Promise.all([
          api.get('/roster/my-shifts'),
          api.get('/roster/open-shifts'),
        ]);
        if (!mounted) return;
        setMyShifts(myRes.data.data || []);
        setOpenShifts(openRes.data.data || []);
      } catch (e) { console.error(e); }
    };
    fetchData();
    api.get('/production/feature-flags/mine').then(r => {
      const flags = r.data?.data || r.data || {};
      if (mounted) setCalendarEnabled(flags.staff_calendar_enabled !== false);
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  const today = format(new Date(), 'yyyy-MM-dd');
  const todayShifts = useMemo(() => myShifts.filter(s => s.date === today), [myShifts, today]);
  const upcoming = useMemo(() => myShifts.filter(s => s.date >= today), [myShifts, today]);

  const requestPickup = async (id) => {
    try {
      await api.post(`/roster/shifts/${id}/pickup-request`);
      toast.success('Pickup request submitted');
      const res = await api.get('/roster/open-shifts');
      setOpenShifts(res.data.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleShiftClick = (s) => navigate(`/portal/shift/${s.id}`);

  const goPrev = () => {
    if (view === 'day') setCurrentDate(d => addDays(d, -1));
    else if (view === 'month') setCurrentDate(d => subMonths(d, 1));
    else setCurrentDate(d => subWeeks(d, 1));
  };
  const goNext = () => {
    if (view === 'day') setCurrentDate(d => addDays(d, 1));
    else if (view === 'month') setCurrentDate(d => addMonths(d, 1));
    else setCurrentDate(d => addWeeks(d, 1));
  };
  const goToday = () => setCurrentDate(new Date());

  const dateLabel = useMemo(() => {
    if (view === 'day') return format(currentDate, 'EEEE, d MMMM yyyy');
    if (view === 'month') return format(currentDate, 'MMMM yyyy');
    if (view === 'list') return '';
    const ws = startOfWeek(currentDate, { weekStartsOn: 1 });
    const we = endOfWeek(currentDate, { weekStartsOn: 1 });
    return `${format(ws, 'd MMM')} – ${format(we, 'd MMM yyyy')}`;
  }, [view, currentDate]);

  // For calendar view, prefetch wider window: include past 30 days + future 90.
  // /roster/my-shifts already returns all assigned shifts; we filter client-side.
  const visibleViewOpts = calendarEnabled ? VIEW_OPTS : VIEW_OPTS.filter(v => v.id === 'list');

  return (
    <div className="space-y-6 animate-fade-in" data-testid="portal-roster-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">My Roster</h1>
        <p className="text-muted-foreground mt-1">Welcome, {user?.full_name}</p>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-3 gap-3">
        <Card><CardContent className="p-4"><Calendar className="h-5 w-5 text-primary mb-2" /><p className="text-2xl font-bold tabular-nums">{upcoming.length}</p><p className="text-xs text-muted-foreground">Upcoming</p></CardContent></Card>
        <Card><CardContent className="p-4"><Clock className="h-5 w-5 text-amber-500 mb-2" /><p className="text-2xl font-bold tabular-nums">{todayShifts.length}</p><p className="text-xs text-muted-foreground">Today</p></CardContent></Card>
        <Card><CardContent className="p-4"><MapPin className="h-5 w-5 text-emerald-500 mb-2" /><p className="text-2xl font-bold tabular-nums">{openShifts.length}</p><p className="text-xs text-muted-foreground">Open</p></CardContent></Card>
      </div>

      {/* View switcher + nav */}
      {calendarEnabled && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="inline-flex rounded-lg border bg-white p-0.5" role="tablist" data-testid="staff-roster-view-switcher">
            {visibleViewOpts.map(v => {
              const Icon = v.Icon;
              const active = view === v.id;
              const cls = active ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100';
              const handle = () => setView(v.id);
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={handle}
                  className={'px-3 py-1.5 text-xs font-medium rounded-md flex items-center gap-1.5 transition ' + cls}
                  data-testid={'staff-roster-view-' + v.id}
                  role="tab"
                  aria-selected={active}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {v.label}
                </button>
              );
            })}
          </div>
          {view !== 'list' && (
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" onClick={goPrev} className="h-8 w-8 p-0" data-testid="staff-roster-prev"><ChevronLeft className="w-4 h-4" /></Button>
              <Button size="sm" variant="outline" onClick={goToday} className="h-8 px-3 text-xs" data-testid="staff-roster-today">Today</Button>
              <Button size="sm" variant="outline" onClick={goNext} className="h-8 w-8 p-0" data-testid="staff-roster-next"><ChevronRight className="w-4 h-4" /></Button>
              <span className="text-xs text-muted-foreground ml-2 tabular-nums" data-testid="staff-roster-date-label">{dateLabel}</span>
            </div>
          )}
        </div>
      )}

      {/* Calendar OR list */}
      {(calendarEnabled && view !== 'list') ? (
        <Card>
          <CardContent className="p-3 sm:p-4">
            <StaffRosterCalendar
              view={view}
              currentDate={currentDate}
              shifts={myShifts}
              onShiftClick={handleShiftClick}
            />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">My Shifts</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Time</TableHead><TableHead>Participant</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {upcoming.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No shifts</TableCell></TableRow>
                : upcoming.map(s => (
                  <TableRow key={s.id} className="hover:bg-muted/60">
                    <TableCell className="font-mono text-xs tabular-nums">{s.date}</TableCell>
                    <TableCell className="font-mono text-xs tabular-nums">{s.start_time}-{s.end_time}</TableCell>
                    <TableCell className="font-medium">{s.participant_name || 'N/A'}</TableCell>
                    <TableCell><StatusBadge status={s.status} /></TableCell>
                    <TableCell>
                      <Button size="sm" variant="outline" onClick={() => navigate(`/portal/shift/${s.id}`)} data-testid={`shift-action-${s.id}`}>
                        {s.status === 'ASSIGNED' ? 'Check In' : s.status === 'IN_PROGRESS' ? 'Check Out' : 'View'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Open Shifts */}
      {openShifts.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Open Shifts Available</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Time</TableHead><TableHead>Participant</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {openShifts.map(s => (
                  <TableRow key={s.id}>
                    <TableCell className="font-mono text-xs">{s.date}</TableCell>
                    <TableCell className="font-mono text-xs">{s.start_time}-{s.end_time}</TableCell>
                    <TableCell>{s.participant_name || 'N/A'}</TableCell>
                    <TableCell>
                      <Button size="sm" variant="secondary" onClick={() => requestPickup(s.id)} data-testid={`pickup-${s.id}`}>Request Pickup</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
